import React, { useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import ChatInput from "./chatInput";
import ChatHeader from "./chatHeader";
import ChatBody from "./chatBody";
import { baseURL } from "../../../services/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import { useChat } from "./ChatProvider";
import TokenManager from "../../../services/TokenManager";
import { streamServiceBookResponse } from "./serviceBookApi";
import { DeepResearchStreamClient } from "./api/DeepResearchStreamClient";
import { DocsChatbotStreamClient } from "./api/DocsChatbotStreamClient";
import { PlannerStreamClient } from "./api/PlannerStreamClient";
import { DocsChatbotMessagesPairExtractor } from "./api/DocsChatbotMessagesPairExtractor";
import { ConversationApiClient } from "./api/ConversationApiClient";
import { DeepChatHistoryStorageManager } from "./api/DeepChatHistoryStorageManager";
import { PlannerChatHistoryStorageManager } from "./api/PlannerChatHistoryStorageManager";
import { ChatApiMessagesBuilder } from "./api/ChatApiMessagesBuilder";

const ChatContainer = styled(Box)(({ theme, fullScreen }) => ({
  width: fullScreen ? "100%" : "100%",
  maxWidth: fullScreen ? "100%" : "480px",
  height: fullScreen ? "100vh" : "680px",
  minHeight: fullScreen ? "89vh" : "680px",
  maxHeight: fullScreen ? "89vh" : "680px",
  borderRadius: fullScreen ? "0" : "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxShadow: fullScreen
    ? "none"
    : "0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 25px rgba(0, 0, 0, 0.08)",
  margin: fullScreen ? "0" : "0 auto",
  paddingBottom: "0",
  position: "relative",
  backgroundColor: "#f8f9fa",
  border: fullScreen ? "none" : "1px solid rgba(24, 116, 210, 0.12)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "& .chat-header": {
    flexShrink: 0,
  },
  "& .chat-body": {
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
  },
  "& .chat-input": {
    flexShrink: 0,
  },
  "@media (max-width: 960px)": {
    width: fullScreen ? "100%" : "100%",
    maxWidth: fullScreen ? "100%" : "100%",
    minWidth: fullScreen ? "auto" : "100%",
    borderRadius: fullScreen ? "0" : "16px",
    height: fullScreen ? "100vh" : "650px",
    minHeight: fullScreen ? "100vh" : "650px",
    maxHeight: fullScreen ? "100vh" : "650px",
  },
  "@media (max-width: 768px)": {
    width: fullScreen ? "100%" : "100%",
    maxWidth: fullScreen ? "100%" : "100%",
    minWidth: fullScreen ? "auto" : "100%",
    borderRadius: fullScreen ? "0" : "16px",
    height: fullScreen ? "100vh" : "600px",
    minHeight: fullScreen ? "100vh" : "600px",
    maxHeight: fullScreen ? "100vh" : "600px",
  },
  "@media (max-width: 600px)": {
    width: "100%",
    maxWidth: "100%",
    minWidth: "100%",
    borderRadius: fullScreen ? "0" : "12px",
    height: fullScreen ? "100vh" : "580px",
    minHeight: fullScreen ? "100vh" : "580px",
    maxHeight: fullScreen ? "100vh" : "580px",
  },
  "@media (max-width: 480px)": {
    width: "100%",
    maxWidth: "100%",
    minWidth: "100%",
    borderRadius: fullScreen ? "0" : "0",
    height: fullScreen ? "100vh" : "100vh",
    minHeight: fullScreen ? "100vh" : "100vh",
    maxHeight: fullScreen ? "100vh" : "100vh",
  },
}));

const ChatWindow = ({ fullScreen, setFullScreen }) => {
  const {
    chatMessages,
    setChatMessages,
    selectedDepartments,
    setSelectedDepartments,
    showInlineContext,
    setShowInlineContext,
    paused,
    setPaused,
    chatMode,
    chatSessionId,
    selectedPerson,
    isServiceBookMode,
    conversationId,
    setConversationId,
    deepHistoryId,
    setDeepHistoryId,
    plannerHistoryId,
    setPlannerHistoryId,
    useMemory,
    setUseMemory,
  } = useChat();
  const bodyRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const deepResearchClientRef = useRef(new DeepResearchStreamClient());
  const docsChatbotClientRef = useRef(new DocsChatbotStreamClient({ baseURL }));
  const plannerClientRef = useRef(new PlannerStreamClient());
  const conversationClientRef = useRef(new ConversationApiClient());
  const deepHistoryStorageRef = useRef(new DeepChatHistoryStorageManager());
  const plannerHistoryStorageRef = useRef(new PlannerChatHistoryStorageManager());
  const apiMessagesBuilderRef = useRef(new ChatApiMessagesBuilder());

  const scrollToBottomNow = () => {
    if (!bodyRef?.current) return;
    const el = bodyRef.current;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } catch (e) {
      el.scrollTop = el.scrollHeight;
    }
  };

  const createMessageTimestamp = () => new Date().toISOString();

  const handleOpenClick = () => {
    if (!fullScreen) {
      navigate("/chatbot");
      return;
    }
    if (location.pathname === "/chatbot") {
      navigate(-1);
    } else {
      setFullScreen(false);
    }
  };

  const handleDepartmentChange = (departments) => {
    setSelectedDepartments(Array.isArray(departments) ? departments : []);
  };

  const updateLastBotMessage = (patch) => {
    setChatMessages((prevChat) => {
      const updated = [...prevChat];
      const lastIndex = updated.length - 1;
      if (updated[lastIndex]?.sender === "bot") {
        const cleanedPatch = {};
        for (const [key, value] of Object.entries(patch || {})) {
          if (value !== undefined) cleanedPatch[key] = value;
        }
        updated[lastIndex] = { ...updated[lastIndex], ...cleanedPatch };
      }
      return updated;
    });
  };

  const ensureBotPlaceholder = () => {
    setChatMessages((prevChat) => [
      ...prevChat,
      {
        sender: "bot",
        text: "",
        isTyping: true,
        isDone: false,
        timestamp: createMessageTimestamp(),
      },
    ]);
    requestAnimationFrame(scrollToBottomNow);
  };

  const handleSend = async (message, outputLang) => {
    if (!message.trim()) return;

    const docsMessagesPairs = new DocsChatbotMessagesPairExtractor().extract(
      chatMessages,
      { maxPairs: 3 },
    );
    const apiHistoryMessages = apiMessagesBuilderRef.current.build(
      chatMessages,
      message,
    );
    setPaused(true);

    // Add user's message
    setChatMessages((prevChat) => [
      ...prevChat,
      { sender: "user", text: message, timestamp: createMessageTimestamp() },
    ]);
    requestAnimationFrame(scrollToBottomNow);

    ensureBotPlaceholder();

    const token = TokenManager.retrieve();
    if (!token) {
      throw new Error("No token found");
    }

    // Ensure a conversation exists for normal mode so backend history can be tracked.
    let activeConversationId = conversationId;
    if (!activeConversationId && chatMode === "normal" && !isServiceBookMode) {
      try {
        const createdId = await conversationClientRef.current.create();
        if (createdId) {
          activeConversationId = createdId;
          setConversationId(createdId);
        }
      } catch {
        // If conversation creation fails, continue without history rather than blocking chat.
      }
    }

    if (chatMode === "deep" && !deepHistoryId) {
      const createdDeepId = deepHistoryStorageRef.current.createSession();
      if (createdDeepId) {
        setDeepHistoryId(createdDeepId);
      }
    }
    let activePlannerHistoryId = plannerHistoryId;
    if (chatMode === "planner" && !activePlannerHistoryId) {
      const createdPlannerId = plannerHistoryStorageRef.current.createSession();
      if (createdPlannerId) {
        activePlannerHistoryId = createdPlannerId;
        setPlannerHistoryId(createdPlannerId);
      }
    }

    // Service Book flow (takes priority when Service_Book gad_type is selected)
    if (isServiceBookMode) {
      try {
        await streamServiceBookResponse({
          message,
          chatSessionId,
          selectedPerson,
          setChatMessages,
        });
      } catch (error) {
        updateLastBotMessage({ text: "", isTyping: false, isDone: true });
      } finally {
        setPaused(false);
      }
      return;
    }

    // Deep Research flow
    if (chatMode === "deep") {
      try {
        let gotAnyDelta = false;
        await deepResearchClientRef.current.stream({
          message,
          chatSessionId,
          messages: apiHistoryMessages,
          selectedDepartments,
          language: outputLang === "mr" ? "ma" : outputLang || "ma",
          useMemory,
          token,
          onDelta: ({ text, thinking, contextArray }) => {
            gotAnyDelta = true;
            updateLastBotMessage({
              text: text || "",
              thinking,
              contextArray,
              isDone: false,
              isTyping: false,
            });
          },
          onDone: ({ contextArray }) => {
            updateLastBotMessage({
              contextArray,
              isDone: true,
              isTyping: false,
            });
          },
          onError: (e) => {
            throw e;
          },
        });

        if (!gotAnyDelta) {
          updateLastBotMessage({ text: "", isTyping: false, isDone: true });
        }
      } catch (error) {
        console.error("Deep research API error:", error);
        updateLastBotMessage({ text: "", isTyping: false, isDone: true });
      } finally {
        setPaused(false);
      }
      return;
    }

    // Planner flow
    if (chatMode === "planner") {
      try {
        let gotAnyDelta = false;
        await plannerClientRef.current.stream({
          message,
          chatSessionId: activePlannerHistoryId || chatSessionId,
          messages: apiHistoryMessages,
          gadType:
            Array.isArray(selectedDepartments) && selectedDepartments.length > 0
              ? selectedDepartments
              : [""],
          language: outputLang === "mr" ? "ma" : outputLang || "ma",
          useMemory,
          outputLang,
          token,
          onDelta: ({ text, thinking, contextArray }) => {
            gotAnyDelta = true;
            updateLastBotMessage({
              text: text ? text : undefined,
              thinking: thinking ? thinking : undefined,
              contextArray:
                Array.isArray(contextArray) && contextArray.length > 0
                  ? contextArray
                  : undefined,
              isDone: false,
              isTyping: false,
            });
          },
          onDone: ({ contextArray }) => {
            updateLastBotMessage({
              contextArray:
                Array.isArray(contextArray) && contextArray.length > 0
                  ? contextArray
                  : undefined,
              isDone: true,
              isTyping: false,
            });
          },
          onError: (e) => {
            if (process.env.NODE_ENV === "development") {
              console.error("Planner stream error:", e);
            }
            const isNetworkError =
              e?.message === "network error" || e?.name === "TypeError";
            const fallbackMessage =
              "Connection was interrupted. Please try again.";
            setChatMessages((prev) => {
              const next = [...prev];
              const lastIndex = next.length - 1;
              if (next[lastIndex]?.sender !== "bot") return prev;
              const current = next[lastIndex];
              const hasContent =
                (current.text && current.text.trim()) ||
                (current.thinking && current.thinking.trim());
              next[lastIndex] = {
                ...current,
                text: hasContent
                  ? (current.text || "") + "\n\n_" + fallbackMessage + "_"
                  : isNetworkError
                    ? fallbackMessage
                    : current.text,
                isTyping: false,
                isDone: true,
              };
              return next;
            });
          },
        });

        if (!gotAnyDelta) {
          updateLastBotMessage({ text: "", isTyping: false, isDone: true });
        } else {
          // Ensure we finalize even if the stream ended without calling onDone.
          updateLastBotMessage({ isDone: true, isTyping: false });
        }
      } catch (error) {
        console.error("Planner API error:", error);
        // Preserve any existing text/thinking; only stop typing + finalize.
        updateLastBotMessage({ isTyping: false, isDone: true });
      } finally {
        setPaused(false);
      }
      return;
    }

    try {
      let currentContext = [];
      let gotAnyDelta = false;
      await docsChatbotClientRef.current.stream({
        message,
        token,
        selectedDepartments,
        messages: docsMessagesPairs,
        useMemory,
        language: outputLang,
        conversationId: activeConversationId,
        onDelta: ({ text, contextArray }) => {
          gotAnyDelta = true;
          if (contextArray) currentContext = contextArray;
          updateLastBotMessage({
            text: text || "",
            contextArray: currentContext,
            isDone: false,
            isTyping: false,
          });
        },
        onDone: ({ contextArray }) => {
          if (contextArray) currentContext = contextArray;
          updateLastBotMessage({
            contextArray: currentContext,
            isDone: true,
            isTyping: false,
          });
        },
        onError: (e) => {
          throw e;
        },
      });

      if (!gotAnyDelta) {
        updateLastBotMessage({ text: "", isTyping: false, isDone: true });
      }
    } catch (error) {
      console.error("Error fetching bot response:", error);
      // Remove typing indicator or show fallback error
      updateLastBotMessage({ text: "", isTyping: false, isDone: true });
    } finally {
      setPaused(false);
    }
  };

  const handlePauseToggle = () => {
    setPaused(!paused);
  };

  useEffect(() => {
    if (chatMode !== "deep") return;
    if (!deepHistoryId) return;
    deepHistoryStorageRef.current.upsertSession({
      id: deepHistoryId,
      messages: chatMessages,
    });
  }, [chatMode, deepHistoryId, chatMessages]);

  useEffect(() => {
    if (chatMode !== "planner") return;
    if (!plannerHistoryId) return;
    plannerHistoryStorageRef.current.upsertSession({
      id: plannerHistoryId,
      messages: chatMessages,
    });
  }, [chatMode, plannerHistoryId, chatMessages]);

  // Add keyboard support for ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && fullScreen) {
        if (location.pathname === "/chatbot") {
          navigate(-1);
        } else {
          setFullScreen(false);
        }
      }
    };

    if (fullScreen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [fullScreen, setFullScreen, location.pathname, navigate]);

  return (
    <>
      <ChatContainer
        className={`chat-container ${fullScreen ? "fullscreen" : ""}`}
        {...{ fullScreen, setFullScreen }}
      >
        <Box
          className="chat-header"
          sx={{ marginBottom: "3px", marginTop: "3px" }}
        >
          <ChatHeader
            handleOpenClick={handleOpenClick}
            fullScreen={fullScreen}
            selectedDepartments={selectedDepartments}
            onDepartmentChange={handleDepartmentChange}
            chatMode={chatMode}
            useMemory={useMemory}
            onUseMemoryChange={setUseMemory}
            showInlineContext={showInlineContext}
            onToggleInlineContext={() => setShowInlineContext((v) => !v)}
          />
        </Box>

        <ChatBody
          chat={chatMessages}
          bodyRef={bodyRef}
          showInlineContext={showInlineContext}
        />
        <Box className="chat-input">
          <ChatInput
            onSend={handleSend}
            loading={paused}
            setLoading={handlePauseToggle}
          />
        </Box>
      </ChatContainer>

      {/* Fullscreen modal removed in favor of dedicated /chatbot route */}
    </>
  );
};

export default ChatWindow;
